<template>
  <div
    class="pet-configurator pet-a11y-container"
    :app-language="selectedLanguage"
    :app-name="$props.source"
    :app-version="version$"
  >
    <template v-if="isReady">
      <system-notifications
        v-if="isApplicationReady"
        :language="selectedLanguage"
      />

      <router-view :route-meta="routeMeta" />

      <div id="modals"></div>
    </template>

    <the-loading
      v-else
      text="Bitte warten | Please wait | S'il vous plaît, attendez | attendere prego"
      forced
    />

    <the-loading />
    <global-error-handler />
    <the-survey />
    <the-snackbar />
  </div>
</template>

<script setup>
import { computed, inject, onMounted, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import sessionStore from '@/store/session'

import useApplication from '@/hooks/useApplication'
import useI18n from '@/hooks/useI18n'
import useSession from '@/hooks/useSession'
import useSync from '@/hooks/useSync'
import useUrl from '@shared/hooks/useUrl'

import TheLoading from '@/components/Container/Loading'
import GlobalErrorHandler from '@/components/Container/GlobalErrorHandler'
import TheSurvey from '@/components/Survey/Survey'
import TheSnackbar from '@/components/Basic/Snackbar'
import SystemNotifications from '@/components/Container/SystemNotifications'

import { SOURCE } from '@/config/events'
import { PET_SESSION_KEY } from '@/config/petConstants'

import '@/assets/css/import.css'

// HOOKS
const { isContentReady, isLanguageLoaded, preferedColorScheme } = useApplication()
const { selectedLanguage } = useI18n()
const { syncStoreAndSessionLead } = useSession()
const { registerSync, unregisterSync } = useSync()
const { react } = useUrl()
const route = useRoute()
const router = useRouter()

// INIT
const version$ = inject('version$')
defineProps({
  source: {
    type: String,
    default: SOURCE.CONFIGURATOR,
  },
})

// DATA
let routeConfirmed = ref(false)

// COMPUTED
const isApplicationReady = computed(() => {
  return routeConfirmed.value && isContentReady.value && isLanguageLoaded.value
})

const isReady = computed(() => {
  return isApplicationReady.value
})

const routeMeta = computed(() => route?.meta || {})

// METHODS
function emitEventWhenReady() {
  return new Promise(function (resolve) {
    if (isApplicationReady.value) resolve()

    const unwatch = watch(
      () => isApplicationReady.value,
      () => {
        unwatch()
        resolve()
      }
    )
  })
}

// LIFECYCLE HOOKS
onMounted(async () => {
  // Only set color scheme in test environments, default to light in production until UX confirmed
  const isTestEnv = import.meta.env.VITE_ENV_KEY !== 'PRD'
  const colorScheme = isTestEnv ? preferedColorScheme() : 'light'
  document.querySelector(':root').style.setProperty('--color-scheme', colorScheme)

  sessionStore.setSessionKey(PET_SESSION_KEY)

  await router.isReady()
  // Initialize lead data from session if available
  await initializeLeadFromSession()

  emitEventWhenReady().then(() => {
    // tab-sync needs to be registered, after all the BE-/localStorage operations are done
    registerSync()

    // Simple tab navigation tracking
    const handleKeyDown = event => {
      if (event.key === 'Tab') {
        document.documentElement.classList.add('using-tab')
      }
    }

    const handleMouseDown = () => {
      document.documentElement.classList.remove('using-tab')
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('mousedown', handleMouseDown)

    // react to URL-params
    window.setTimeout(() => {
      react()
    }, 50)
  })
})

async function initializeLeadFromSession() {
  try {
    const isReentryFlow = sessionStore.isReentryFlow()
    await syncStoreAndSessionLead(isReentryFlow)
  } catch (error) {
    console.warn('Failed to initialize lead from session:', error)
    sessionStore.clearSession({ silent: true })
  }
}

onBeforeUnmount(() => {
  unregisterSync()
})

router.isReady().then(() => {
  routeConfirmed.value = true
})
</script>

<style name="global">
:root {
  /*
    New stacking contexts can be formed on an element in one of three ways:
    - When an element is the root element of a document (the <html> element)
    - When an element has a position value other than static and a z-index value other than auto
    - When an element has an opacity value less than 1
  */
  --pet-stack-level-element: 1;
  --pet-stack-level-footer: 1;
  --pet-stack-level-header: 2;
  --pet-stack-level-popup-menu: 2;
  --pet-stack-level-modal: 3;
  --pet-stack-level-modal-stacked: 4;
  --pet-stack-level-popup-notification: 5;
  --pet-stack-level-tooltip: 6;
  --pet-stack-level-system-notification: 7;
  --pet-stack-level-screen-blocker: 8;
  --pet-stack-level-progress-modal: 9;

  color-scheme: var(--color-scheme, 'light');
}

*,
*:after,
*:before {
  box-sizing: border-box;
}

*:focus {
  outline: none;
}

html {
  overflow-y: scroll;
}

body {
  font-family: var(--font-family-open-sans);
  font-style: var(--font-style-default);
  font-weight: var(--font-weight-regular);
  font-size: var(--font-size-regular-lg);
  letter-spacing: var(--letter-spacing-regular-lg);
  color: var(--Text-Primary);
  background-color: var(--surface);

  margin: 0;
  /* FLEX-2954 prevent larger fonts for mobile landscape */
  -webkit-text-size-adjust: none;
}

p,
h1,
h2,
h3 {
  margin-block-start: 0;
  margin-block-end: 0;
}

a {
  color: inherit;
  text-decoration-line: none;
}

button,
input,
select,
textarea {
  font-family:
    Open Sans,
    sans-serif !important;
}

.tippy-box[data-theme~='pet'] {
  min-width: 144px;
  padding: var(--fixed-spacing-fix-06);
  background-color: var(--surface);
  border-radius: var(--fixed-border-radius-fix-02);
  box-shadow: var(--elevation-level-5);

  .tippy-content {
    font-weight: var(--font-weight-regular);
    font-size: var(--font-size-regular-sm);
    letter-spacing: var(--letter-spacing-regular-sm);
    color: var(--on-surface);
    padding: 0;
  }

  .tippy-arrow {
    color: var(--surface);

    &::before {
      transform: scale(1.25);
    }
  }
}

.tippy-box[data-theme~='card'] {
  background-color: var(--surface);
  border-radius: var(--fixed-border-radius-fix-02);
  box-shadow: var(--elevation-level-5);
  padding: var(--fixed-spacing-fix-02);
  margin: 0;
  & > .tippy-content {
    padding: 0;
    margin: 0;
    height: max-content;

    & > .card__image {
      margin: 0;
      padding: 0;
      width: 100%;
    }
  }

  .tippy-arrow {
    color: var(--surface);

    &::before {
      transform: scale(1.25);
    }
  }
}

.tippy-box[data-placement='top-end'] > {
  .tippy-arrow {
    right: 20px !important;
    left: initial !important;
  }
}

.the-transition-enter-active,
.the-transition-leave-active {
  transition: opacity 0.3s ease-in-out;
}

.the-transition-enter-from,
.the-transition-leave-to {
  opacity: 0;
}
</style>

<style scoped>
.pet-configurator {
  display: grid;
  grid-template-areas:
    'alerts'
    'header'
    'content'
    'footer';
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto auto 1fr auto;
  height: 100vh;
}
</style>
